///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import Konva from "konva";
import { IMarkupRectangle, IMarkupRectangleParams } from "../IMarkupRectangle";
import { IWorldTransform } from "../IWorldTransform";
import { WorldTransform } from "../WorldTransform";

export class KonvaRectangle implements IMarkupRectangle {
  private _ref: Konva.Rect;
  private _worldTransformer: IWorldTransform;

  constructor(params: IMarkupRectangleParams, ref = null, worldTransformer = new WorldTransform()) {
    this._worldTransformer = worldTransformer;

    if (ref) {
      this._ref = ref;
      const wcsStart = this._ref.getAttr("wcsStart");
      const wcsEnd = this._ref.getAttr("wcsEnd");

      if (!wcsStart) {
        this._ref.setAttr("wcsStart", this._worldTransformer.screenToWorld({ x: ref.x(), y: ref.y() }));
      }
      if (!wcsEnd) {
        const rightBottomPoint = { x: ref.x() + ref.width(), y: ref.y() + ref.height() };
        this._ref.setAttr(
          "wcsEnd",
          this._worldTransformer.screenToWorld({ x: rightBottomPoint.x, y: rightBottomPoint.y })
        );
      }
      return;
    }

    if (!params) params = {};
    if (!params.position) params.position = { x: 0, y: 0 };
    if (params.position2) {
      params.width = params.position.x - params.position2.x;
      params.height = params.position.y - params.position2.y;
    } else {
      if (!params.width || !params.height) {
        params.position2 = { x: 200, y: 200 };
        params.width = 200;
        params.height = 200;
      } else {
        params.position2 = { x: params.position.x + params.width, y: params.position.y + params.height };
      }
    }

    this._ref = new Konva.Rect({
      stroke: params.color ?? "#ff0000",
      strokeWidth: params.lineWidth ?? 4,
      globalCompositeOperation: "source-over",
      lineCap: "round",
      lineJoin: "round",
      x: params.position.x,
      y: params.position.y,
      width: params.width ?? 200,
      height: params.height ?? 200,
      draggable: true,
      strokeScaleEnabled: false,
    });

    this._ref.setAttr("wcsStart", this._worldTransformer.screenToWorld({ x: params.position.x, y: params.position.y }));
    this._ref.setAttr("wcsEnd", this._worldTransformer.screenToWorld({ x: params.position2.x, y: params.position2.y }));

    this._ref.on("transform", (e) => {
      const attrs = e.target.attrs;
      const scaleByX = Math.abs(attrs.scaleX - 1) > 10e-6;
      const scaleByY = Math.abs(attrs.scaleY - 1) > 10e-6;

      let newWidth = this._ref.width();
      if (scaleByX) newWidth *= attrs.scaleX;
      let newHeight = this._ref.height();
      if (scaleByY) newHeight *= attrs.scaleY;

      const minWidth = 50;
      const minHeight = 50;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      if (scaleByX) {
        this._ref.width(newWidth);
      }

      if (scaleByY) {
        this._ref.height(newHeight);
      }

      this._ref.scale({ x: 1, y: 1 });
    });

    this._ref.on("transformend", (e) => {
      const attrs = e.target.attrs;
      if (attrs.rotation !== this._ref.rotation()) this._ref.rotation(attrs.rotation);

      const absoluteTransform = this._ref.getStage().getAbsoluteTransform();
      const position = absoluteTransform.point({ x: this._ref.x(), y: this._ref.y() });
      this._ref.setAttr("wcsStart", this._worldTransformer.screenToWorld(position));
      this._ref.setAttr(
        "wcsEnd",
        this._worldTransformer.screenToWorld({ x: position.x + this._ref.width(), y: position.y + this._ref.height() })
      );
    });

    this._ref.on("dragend", (e) => {
      const absoluteTransform = this._ref.getStage().getAbsoluteTransform();
      const position = absoluteTransform.point({ x: this._ref.x(), y: this._ref.y() });
      this._ref.setAttr("wcsStart", this._worldTransformer.screenToWorld(position));
      this._ref.setAttr(
        "wcsEnd",
        this._worldTransformer.screenToWorld({ x: position.x + this._ref.width(), y: position.y + this._ref.height() })
      );
    });

    this._ref.id(this._ref._id.toString());
  }

  getPosition(): { x: number; y: number } {
    return this._ref.position();
  }

  getWidth(): number {
    return this._ref.width();
  }

  getHeigth(): number {
    return this._ref.height();
  }

  setWidth(w: number): void {
    this._ref.width(w);

    const rightLowerPoint = { x: this._ref.x() + w, y: this._ref.y() + this._ref.height() };
    const wcsRightLowerPoint = this._worldTransformer.screenToWorld(rightLowerPoint);
    this._ref.setAttr("wcsEnd", wcsRightLowerPoint);
  }

  setHeight(h: number): void {
    this._ref.height(h);

    const rightLowerPoint = { x: this._ref.x() + this._ref.width(), y: this._ref.y() + h };
    const wcsRightLowerPoint = this._worldTransformer.screenToWorld(rightLowerPoint);
    this._ref.setAttr("wcsEnd", wcsRightLowerPoint);
  }

  setPosition(x: number, y: number): void {
    this._ref.setPosition({ x, y });
    this._ref.setAttr("wcsStart", this._worldTransformer.screenToWorld({ x, y }));
  }

  ref() {
    return this._ref;
  }

  id(): string {
    return this._ref.id();
  }

  enableMouseEditing(value: boolean): void {
    this._ref.draggable(value);
  }

  type(): string {
    return "Rectangle";
  }

  getColor(): string {
    return this._ref.stroke() as string;
  }

  setColor(hex: string): void {
    this._ref.stroke(hex);
  }

  getRotation(): number {
    return this._ref.rotation();
  }

  setRotation(degrees: number): void {
    this._ref.rotation(degrees);
  }

  getZIndex(): number {
    return this._ref.zIndex();
  }

  setZIndex(zIndex: number): void {
    this._ref.zIndex(zIndex);
  }

  delete(): void {
    this._ref.destroy();
    this._ref = null;
  }

  setLineWidth(size: number): void {
    this._ref.strokeWidth(size);
  }

  getLineWidth(): number {
    return this._ref.strokeWidth();
  }

  updateScreenCoordinates(): void {
    let screenPositionStart = this._worldTransformer.worldToScreen(this._ref.getAttr("wcsStart"));
    let screenPositionEnd = this._worldTransformer.worldToScreen(this._ref.getAttr("wcsEnd"));

    let invert = this._ref.getStage().getAbsoluteTransform().copy();
    invert = invert.invert();
    const positionStart = invert.point(screenPositionStart);
    const positionEnd = invert.point(screenPositionEnd);

    this._ref.position({ x: positionStart.x, y: positionStart.y });
    this._ref.width(Math.abs(positionEnd.x - positionStart.x));
    this._ref.height(Math.abs(positionEnd.y - positionStart.y));
  }
}
